import { Button, Dialog, DialogActions, DialogTitle, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid/locales';
import {
  DataGridPremium, GridColumnResizeParams,
  GridFilterItem,
  GridFilterModel,
  GridRowSelectionModel,
  GridSortModel
} from '@mui/x-data-grid-premium';
import { FC, memo, useContext, useEffect, useMemo, useState } from 'react';
import { CustomLoadingOverlay } from 'src/components/utils/CustomLoadingOverlay';
import LicenseContext from 'src/contexts/LicenseContext';
import { DriverEntity } from 'src/entities/driverEntity';
import { RansackPredicateEntity } from 'src/entities/RansackPredicate.entity';
import { useDriversRequest as usePaginationRequest } from 'src/hooks/useDrivers.request';
import operatorValueUtil from 'src/utils/operatorValue.util';
import stringUtil from 'src/utils/string.util';

import { Columns } from '../V2DriversPresenter/Columns';

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (driverEntities: DriverEntity[]) => void;
  selectedCompanyId: number;
  currentDriverIds: number[];
};

const SelectDriverDialog: FC<Props> = memo((
  {
    open,
    onClose,
    onSelect,
    currentDriverIds,
    selectedCompanyId,
  }
) => {
  const licenseContext = useContext(LicenseContext);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(30);
  const [rowCount, setRowCount] = useState<number>(0);
  const [sortCondition, setSortCondition] = useState<string>('');
  const [orConditions, setOrConditions] = useState<RansackPredicateEntity[]>([]);
  const [andConditions, setAndConditions] = useState<RansackPredicateEntity[]>([]);
  const [searchCondition, setSearchCondition] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { data: requestData, isLoading: requestIsLoading } = usePaginationRequest(
    0,
    sortCondition,
    searchCondition,
    Number.MAX_SAFE_INTEGER
  );

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length === 0) {
      setSortCondition('');
      return;
    }

    setSortCondition(
      [
        stringUtil.toSnakeCase(sortModel[0].field),
        sortModel[0].sort
      ].join('+')
    );
  };

  const onFilterChange = ((filterModel: GridFilterModel) => {
    const items = filterModel?.items;

    if (!items || items.length === 0) {
      setOrConditions([]);
      setAndConditions([]);
      return;
    }

    const ransackPredicates: RansackPredicateEntity[] = items.flatMap((item: GridFilterItem) => operatorValueUtil.convertToRansackPredicate(
      stringUtil.toSnakeCase(item.field),
      item.operator,
      item.value as string | string[] | undefined,
      filterModel.logicOperator
    ));

    setOrConditions(ransackPredicates.filter((predicate) => predicate?.link === 'or'));
    setAndConditions(ransackPredicates.filter((predicate) => predicate?.link === 'and'));
  });

  const dataGridColumns: GridColDef[] = useMemo(() => Columns().filter((col) => {
    if (licenseContext?.config?.unit_used_for_calculation === '重量と体積') {
      return true;
    }

    return col.field !== 'loading_platform_volume_m3';
  }), [licenseContext?.config]);

  const onSelectionModelChange = (rowSelectionModel: GridRowSelectionModel) => {
    const ids = Array.from(rowSelectionModel.ids);
    if (rowSelectionModel.type === 'include') {
      setSelectedIds(ids.map((it) => Number(it)));
    } else if (rowSelectionModel.type === 'exclude') {
      const allIds = requestData.data.map((it) => it.id);
      setSelectedIds(allIds.filter((id) => !ids.includes(id)));
    }
  };

  const maybeSelectedDrivers = requestData?.data.filter((e) => selectedIds.includes(e.id)) || [];

  const onColumnWidthChange = (params: GridColumnResizeParams) => {
    const columns = dataGridColumns.map((col) => {
      let newDefinition = { field: col.field, width: col.width };
      if (col.field === params.colDef.field) {
        newDefinition = { ...newDefinition, width: params.colDef.width };
      }
      return newDefinition;
    });
    localStorage.setItem('truck-columns-width', JSON.stringify(columns));
  };

  const onSubmit = () => {
    const selectedEntities = requestData?.data.filter((e) => selectedIds.includes(e.id)) || [];
    onSelect(selectedEntities);
    onClose();
  };

  useEffect(() => {
    const maybeCompanyPredicateEntity: RansackPredicateEntity | null = selectedCompanyId ? {
      column: 'company_id',
      predicate: 'eq',
      value: `${selectedCompanyId}`,
      link: 'and'
    } : null;

    const modifiedAndConditions: RansackPredicateEntity[] = maybeCompanyPredicateEntity
      ? [
        maybeCompanyPredicateEntity,
        ...andConditions
      ] : andConditions;

    setSearchCondition(
      [
        operatorValueUtil.convertToQueryParams('or_conditions', orConditions),
        operatorValueUtil.convertToQueryParams('and_conditions', modifiedAndConditions),
      ].filter((maybe) => maybe).join('&')
    );
  }, [andConditions, orConditions, selectedCompanyId]);

  useEffect(() => {
    if (!requestData) return;
    setRowCount(requestData?.totalCount || 0);
  }, [requestData]);

  useEffect(() => {
    if (currentDriverIds) {
      setSelectedIds(currentDriverIds);
      return;
    }

    setSelectedIds([]);
  }, [currentDriverIds]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      sx={{
        mt: 8
      }}
    >
      <DialogTitle>
        <Typography variant="h6">
          ドライバーを選択してください
        </Typography>
      </DialogTitle>
      <DataGridPremium
        rows={requestData?.data || []}
        columns={dataGridColumns}
        pagination
        checkboxSelection
        disableRowGrouping
        initialState={{
          columns: {
            columnVisibilityModel: {
              garage_address: false,
            },
          },
        }}
        slots={{
          loadingOverlay: CustomLoadingOverlay,
        }}
        localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
        loading={requestIsLoading}
        onColumnWidthChange={(params) => onColumnWidthChange(params)}
        onRowSelectionModelChange={onSelectionModelChange}
        sortingMode="server"
        paginationMode="client"
        filterMode="server"
        rowCount={rowCount}
        onSortModelChange={handleSortModelChange}
        onFilterModelChange={onFilterChange}
        rowSelectionModel={{ ids: new Set(selectedIds), type: 'include' }}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model) => {
          setPage(model.page);
          setPageSize(model.pageSize);
          setSelectedIds([]);
        }}
        sx={{
          mb: 2,
          mx: 2,
        }}
        keepNonExistentRowsSelected
        disableAggregation
        disablePivoting
      />
      <DialogActions>
        <Button
          onClick={onClose}
          color="primary"
        >
          キャンセル
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          color="primary"
        >
          {
            (() => {
              if (!maybeSelectedDrivers) return '設定する';
              if (maybeSelectedDrivers.length === 0) return '設定する';
              if (maybeSelectedDrivers.length === 1) return `${maybeSelectedDrivers[0].name} を設定する`;
              const lastSelectedDriver = [...maybeSelectedDrivers].slice(-1)[0];
              const selectedOtherDriverLength = maybeSelectedDrivers.length - 1;

              return `${lastSelectedDriver.name} 他${selectedOtherDriverLength}名を設定する`;
            })()
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default SelectDriverDialog;
